import type { ReactNode } from 'react';
import { TableCell, type TableCellType } from '../../molecules/TableCell/TableCell';
import { TableHeaderCell } from '../../molecules/TableHeaderCell/TableHeaderCell';
import type { TableHeaderSort } from '../../molecules/TableHeaderLabel/TableHeaderLabel';
import styles from './Table.module.css';

/**
 * Figma: `Table` (323:6691) — "Full-featured data table with header bar,
 * sortable columns, and pagination… Card-style container with rounded corners
 * and shadow works standalone or within dashboard layouts."
 *
 * Three variant axes (`Example`, `Breakpoint`, `Dividers`) have a single option
 * each, so none becomes a prop. `Pagination` is a Figma boolean for "is the
 * footer there", which here is simply whether a `pagination` node was passed.
 *
 * `Type` is the one real variant axis: `Default`, `Loading` and `Empty` (the
 * last two new 2026-09-21). `Empty` is built and is the `type` prop; `Loading`
 * is not yet — nothing in the app has a table that loads.
 *
 * Figma builds the body column-major — `Content` is a horizontal row of
 * `Column` frames, each stacking its header cell above its data cells — because
 * that is how auto-layout keeps a column's width consistent. HTML has a
 * purpose-built mechanism for that, so this is a real `<table>`: rows are rows,
 * `<th>` heads its column, and a screen reader gets the row/column
 * relationships that the Figma structure only implies.
 */
export type TableColumn = {
  key: string;
  /** Figma `Text=True` on the header cell. Omit for a spacer column. */
  header?: string;
  sort?: TableHeaderSort;
  helpText?: string;
  onSort?: () => void;
  /** Figma `Checkbox=True` — the select-all control. */
  headerCheckbox?: ReactNode;
  /** Figma `Type` for this column's body cells. */
  cellType?: TableCellType;
  /**
   * Any CSS width. Setting it on *any* column switches the table to
   * `table-layout: fixed`, so the widths given are the widths used and columns
   * left without one share what is left.
   *
   * Not in Figma, which draws proportioned columns without saying how they got
   * that way. Needed because a cell holding a long unwrapped string sizes its
   * column to the whole string under the default `auto` layout — the text
   * truncates only once something upstream fixes the width.
   */
  width?: string;
  /**
   * Size the column to its widest cell and never truncate it — for short labels
   * (an intent, a date) that a reader has to see whole. Not in Figma.
   *
   * A table with a `fit` column leaves `fixed` layout: the fit columns hug their
   * content, columns with a `width` keep it, and the **one column with neither**
   * takes the rest and is the one that truncates. Without such a column the
   * table has nothing to absorb the slack and the fit columns just share it.
   */
  fit?: boolean;
  /**
   * On hover, show the whole text in a tooltip when the cell has clipped it.
   * For the column that truncates — a post's text — so what is cut off is still
   * reachable. Not in Figma.
   */
  reveal?: boolean;
};

/** One cell's content, mirroring `TableCell`'s own props. */
export type TableCellData = {
  /** Figma `Lead text`. */
  text?: string;
  /** Figma `Supporting text`. */
  supportingText?: string;
  /** Anything that is not plain text — a badge, progress bar, buttons. */
  content?: ReactNode;
  /** Figma `Lead action` — the row's checkbox, radio or toggle. */
  leadAction?: ReactNode;
};

export type TableRowData = {
  key: string;
  /** Figma `State=Hover/selected`. */
  selected?: boolean;
  cells: Record<string, TableCellData>;
  /**
   * Makes the row open something: a click **anywhere on the row** runs it, and
   * the first column's text becomes the row's link — `text/link`, underlined, on
   * hover, as Figma draws the hovered row of Competitive page performance
   * (2024:32704, 2026-09-21). That text is a real button, so the action has a
   * keyboard stop; a clickable `<tr>` alone would have none.
   *
   * Clicks on the row's other controls — a `View ↗`, a checkbox, a select —
   * stay theirs and do not also open the row. Neither does a click that ends a
   * text selection, so a reader can still copy a figure out of the table.
   */
  onActivate?: () => void;
  /**
   * The row link's accessible name, when the first column's text alone does
   * not say what the row opens. Not in Figma.
   */
  activateLabel?: string;
};

/**
 * Whether a click on a row should open it: yes on plain cell content and on the
 * row's own link, no on any other interactive control inside it.
 */
function opensRow(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true;
  const control = target.closest('button, a, input, select, textarea, label, [role="combobox"]');
  return control === null || control.hasAttribute('data-row-link');
}

export type TableProps = {
  columns: TableColumn[];
  rows: TableRowData[];
  /** Figma's `Table Header` instance — the title bar. */
  header?: ReactNode;
  /**
   * Figma `Filters#2027:6` — the `Tables Filters Bar` row between the title bar
   * and the grid, new 2026-09-18 with the Competitors `Tune sentiment` modal
   * (2037:7616). This is its leading content — a `Dropdown` there, `View: All`.
   * The row renders when either this or `filterActions` is passed, which is the
   * repo's reading of a Figma boolean.
   *
   * Slots rather than typed props, for `TableHeader`'s reason: Figma fills them
   * with whole instances, and naming them here would re-describe components this
   * one does not own.
   */
  filters?: ReactNode;
  /**
   * The same row's trailing `Actions Wrapper` — three Tertiary buttons,
   * `space/12` apart, in the frame that introduced it.
   */
  filterActions?: ReactNode;
  /** Figma `Pagination` — the footer. Omit to render without one. */
  pagination?: ReactNode;
  /**
   * Figma `Type`. `empty` is `Type=Empty` (2109:26403): the title bar and then
   * one centred line where the grid would be — **no column headers and no
   * footer**, because the variant draws neither. `columns` and `rows` are
   * ignored while it is set; the line is `emptyLabel`, defaulting to the
   * variant's own "No data available".
   *
   * Distinct from `emptyLabel` on a `default` table, which keeps the headers
   * and draws a single row-sized band. That one answers "your search matched
   * nothing" under columns the user is still reading; this one answers "there
   * is nothing here yet", where a row of column names over no rows is noise.
   */
  type?: 'default' | 'empty';
  /**
   * The `shadow/xs` lift. Off by default — a table on the page is flat — and
   * on for a table inside a modal (the user's call, 2026-09-21).
   */
  elevated?: boolean;
  /**
   * Accessible name for the table. Not in Figma — a table with no caption or
   * name is just a grid of strings to a screen reader.
   */
  label?: string;
  /**
   * Shown in place of the rows when `rows` is empty. Not in Figma, which draws
   * no empty table — but a header holding a `Search` field can filter every row
   * away, and a table that answers that with a blank band says nothing about
   * whether it is loading, broken, or simply matched nothing.
   *
   * Opt-in: omit it and an empty table renders as it always did.
   *
   * With `type="empty"` it is the variant's centred line instead.
   */
  emptyLabel?: string;
  className?: string;
};

export function Table({
  columns,
  rows,
  header,
  filters,
  filterActions,
  pagination,
  type = 'default',
  elevated = false,
  label,
  emptyLabel,
  className,
}: TableProps) {
  const hasWidths = columns.some((column) => column.width !== undefined);
  const hasFit = columns.some((column) => column.fit);
  const hasFilters = filters !== undefined || filterActions !== undefined;

  if (type === 'empty') {
    return (
      <div
        className={[styles.card, elevated ? styles.elevated : null, className]
          .filter(Boolean)
          .join(' ')}
      >
        {header}
        {hasFilters && (
          <div className={styles.filters}>
            <div className={styles.filtersStart}>{filters}</div>
            {filterActions !== undefined && (
              <div className={styles.filterActions}>{filterActions}</div>
            )}
          </div>
        )}
        {/* Figma `Frame 1` — the variant's whole body. */}
        <div className={styles.emptyBody}>
          <p className={styles.emptyMessage}>{emptyLabel ?? 'No data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={[styles.card, elevated ? styles.elevated : null, className]
        .filter(Boolean)
        .join(' ')}
    >
      {header}

      {/* Figma `Tables Filters Bar` — leading content, then the actions, pushed apart. */}
      {hasFilters && (
        <div className={styles.filters}>
          <div className={styles.filtersStart}>{filters}</div>
          {filterActions !== undefined && (
            <div className={styles.filterActions}>{filterActions}</div>
          )}
        </div>
      )}

      {/*
       * Not in Figma, which draws one desktop width. A table narrower than its
       * columns has to scroll somewhere, and scrolling the body rather than the
       * page keeps the title bar and pagination in place.
       */}
      <div className={styles.scroll}>
        <table
          className={[styles.table, hasWidths && !hasFit && styles.fixed].filter(Boolean).join(' ')}
          aria-label={label}
        >
          {/* A `<colgroup>` rather than a width on each `<th>`: the width belongs
              to the column, and stating it once keeps it off every body cell. */}
          {(hasWidths || hasFit) && (
            <colgroup>
              {columns.map((column) => (
                <col
                  key={column.key}
                  /* `1%` is "no wider than the content needs": with the cell's `nowrap`, the column's minimum. */
                  style={
                    column.fit
                      ? { width: '1%' }
                      : column.width
                        ? { width: column.width }
                        : undefined
                  }
                />
              ))}
            </colgroup>
          )}

          <thead>
            <tr>
              {columns.map((column) => (
                <TableHeaderCell
                  key={column.key}
                  sort={column.sort}
                  helpText={column.helpText}
                  checkbox={column.headerCheckbox}
                  onSort={column.onSort}
                >
                  {column.header}
                </TableHeaderCell>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && emptyLabel !== undefined && (
              <tr>
                <td className={styles.empty} colSpan={columns.length}>
                  <span>{emptyLabel}</span>
                </td>
              </tr>
            )}
            {rows.map((row) => (
              /* `aria-selected` is not in Figma — the design has a surface for
                 selection but no way to say it out loud. */
              <tr
                key={row.key}
                aria-selected={row.selected || undefined}
                className={row.onActivate ? styles.activatable : undefined}
                onClick={
                  row.onActivate
                    ? (event) => {
                        if (!opensRow(event.target)) return;
                        /* A drag that selected text is a copy, not a click. */
                        if (window.getSelection()?.toString()) return;
                        row.onActivate?.();
                      }
                    : undefined
                }
              >
                {columns.map((column, index) => {
                  const cell = row.cells[column.key];
                  return (
                    <TableCell
                      key={column.key}
                      type={column.cellType}
                      fit={column.fit}
                      fill={hasFit && !column.fit && column.width === undefined}
                      reveal={column.reveal}
                      selected={row.selected}
                      text={cell?.text}
                      supportingText={cell?.supportingText}
                      leadAction={cell?.leadAction}
                      /* The leading column carries the row's link. */
                      rowLink={
                        row.onActivate !== undefined && index === 0 && cell?.text !== undefined
                      }
                      rowLinkLabel={index === 0 ? row.activateLabel : undefined}
                    >
                      {cell?.content}
                    </TableCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination}
    </div>
  );
}
